import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MediaDevicePermissionWatcherService {
  private microphonePermission = new BehaviorSubject<string>('prompt');
  private cameraPermission = new BehaviorSubject<string>('prompt');
  private _permissionsRefreshed = new BehaviorSubject<unknown>(null);

  public readonly microphonePermission$ = this.microphonePermission.asObservable();
  public readonly cameraPermission$ = this.cameraPermission.asObservable();
  public readonly permissionsRefreshed$ = this._permissionsRefreshed.asObservable();

  constructor() {
    // It's good practice to check permissions when the service is first initialized.
    this.checkPermissions();
  }

  public get microphonePermissionState(): string {
    return this.microphonePermission.value;
  }

  public get cameraPermissionState(): string {
    return this.cameraPermission.value;
  }

  public requestMicrophoneAccess(): Promise<boolean> {
    return new Promise(async (resolve) => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        tempStream.getTracks().forEach((track) => track.stop());
        this.checkPermissions(); // Re-check permissions after the user has made a choice.
        resolve(true);
      } catch (error) {
        this.checkPermissions(); // Also re-check on error (e.g., user denies).
        resolve(false);
      }
    });
  }

  public requestCameraAccess(): Promise<boolean> {
    return new Promise(async (resolve) => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true,
        });
        tempStream.getTracks().forEach((track) => track.stop());
        this.checkPermissions();
        resolve(true);
      } catch (error) {
        this.checkPermissions();
        resolve(false);
      }
    });
  }

  public requestCameraAndMicrophoneAccess(): Promise<boolean> {
    return new Promise(async (resolve) => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        tempStream.getTracks().forEach((track) => track.stop());
        this.checkPermissions();
        resolve(true);
      } catch (error) {
        this.checkPermissions();
        resolve(false);
      }
    });
  }

  /**
   * Checks the current status of camera and microphone permissions and notifies subscribers.
   */
  public async checkPermissions(): Promise<void> {
    try {
      // Query both permissions. Using 'any' for name as it's still not in all TS libs.
      const micStatus = await navigator.permissions.query({ name: 'microphone' as any });
      const camStatus = await navigator.permissions.query({ name: 'camera' as any });

      // Update the BehaviorSubjects with the current states.
      this.microphonePermission.next(micStatus.state);
      this.cameraPermission.next(camStatus.state);

      // Set up onchange listeners to automatically update on future changes.
      micStatus.onchange = () => this.microphonePermission.next(micStatus.state);
      camStatus.onchange = () => this.cameraPermission.next(camStatus.state);

      // --- This is the new, crucial part ---
      // Notify any subscribers (like the DeviceSelectorComponent) that a check has completed.
      this._permissionsRefreshed.next(null);
    } catch (error) {
      console.error("Permission query failed. This can happen in browsers like Firefox where it's not fully supported.", error);
      // Even on error, we can notify to attempt a fallback.
      this._permissionsRefreshed.next(null);
    }
  }
}
